// File: src/signaling.js

import { connectedPeers, updatePeers } from "./peers";
import { handleIncomingSignal } from "./handleIncomingSignal";

let socket = null;
let isNegotiating = {}; // Track negotiation status per peer
let remoteTrackCallback = null; // Callback to send remote tracks to App.js

export const setRemoteTrackCallback = (callback) => {
  remoteTrackCallback = callback;
};

export const getRemoteTrackCallback = () => {
  return remoteTrackCallback;
};

// ✅ Inject socket from context
export const setSocketForSignaling = (s) => {
  socket = s;

  // Reset negotiation tracking on connect/disconnect
  socket.on("connect", () => {
    isNegotiating = {};
  });

  socket.on("disconnect", () => {
    isNegotiating = {};
  });

  socket.on("client_list", ({ clients }) => {
  console.log("🧙 Received client list:", clients);
  updatePeers(clients);
});

  socket.on("signal", (data) => {
    const peerConnection = getPeerConnection(data.sender);
    if (!peerConnection) {
      console.warn(`⚠️ No peer connection found for sender: ${data.sender}`);
      return;
    }

    handleIncomingSignal(data, socket, peerConnection);
  });
};

// 🔍 Safely fetch peer connection, creating one if missing
function getPeerConnection(peerId) {
  if (!connectedPeers.has(peerId)) {
    const { addPeerForClient } = require("./peers");
    addPeerForClient(peerId);
  }

  const peerData = connectedPeers.get(peerId);
  if (!peerData?.peerConnection) {
    console.warn(`⏳ PeerConnection for ${peerId} not yet ready.`);
    return null;
  }

  return peerData.peerConnection;
}

// 📡 Start signaling for all connected peers
export function startSignaling() {
  if (!socket) {
    console.error("❌ Cannot start signaling: socket not set");
    return;
  }

  connectedPeers.forEach(async (peerData, peerId) => {
    if (peerId === socket.id) return;

    const peerConnection = peerData.peerConnection;
    await sendOffer(peerId, peerConnection);
  });
}

// 📡 Send WebRTC Offer to a specific peer
export async function sendOffer(peerId, peerConnection) {
  if (!socket) {
    console.error("❌ Cannot send offer: socket not set");
    return;
  }

  if (!peerConnection) {
    console.error(`⚠️ Cannot create offer — peerConnection missing for ${peerId}`);
    return;
  }

  if (isNegotiating[peerId]) {
    console.warn(`⛔ sendOffer skipped — negotiation already in progress for ${peerId}`);
    return;
  }

  if (peerConnection.signalingState !== "stable") {
    console.warn(
      `⏳ Cannot create offer — signaling state for ${peerId} is not stable: ${peerConnection.signalingState}`
    );
    return;
  }

  isNegotiating[peerId] = true;

  try {
    peerConnection.oniceconnectionstatechange = () => {
      console.log(`🌐 ICE connection state (${peerId}):`, peerConnection.iceConnectionState);
    };

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    setTimeout(() => {
      peerConnection.getStats(null).then((stats) => {
        stats.forEach((report) =>
          console.log("📊 ICE candidate gathering check:", report)
        );
      });
    }, 1000);

    let attempts = 0;
    while (peerConnection.signalingState === "have-local-offer" && attempts < 10) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    if (
      peerConnection.signalingState !== "stable" &&
      peerConnection.signalingState !== "have-local-offer"
    ) {
      console.warn(
        `⚠️ Unexpected signaling state after offer: ${peerConnection.signalingState}`
      );
    }

    socket.emit("signal", {
      type: "offer",
      target: peerId,
      sender: socket.id,
      offer: peerConnection.localDescription,
    });

    if (peerConnection.pendingIceCandidates?.length) {
      peerConnection.pendingIceCandidates.forEach((candidate) => {
        peerConnection
          .addIceCandidate(new RTCIceCandidate(candidate))
          .then(() => console.log("✅ ICE candidate successfully added!"))
          .catch((err) => console.error("❌ Error adding ICE candidate:", err));
      });
      peerConnection.pendingIceCandidates = [];
    }
  } catch (error) {
    console.error(`❌ Error creating or sending offer to ${peerId}:`, error);
  } finally {
    isNegotiating[peerId] = false;
  }
}
